import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaLock } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import apiClient from '../utils/apiClient';
import config from '../config';

const Login = ({ onLoginSuccess, onRegisterClick, onForgotPasswordClick, isDarkMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);



useEffect(() => {
  // Check for OAuth tokens or authorization code in URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const accessToken = urlParams.get('access_token');
  const refreshToken = urlParams.get('refresh_token');
  const authSuccess = urlParams.get('auth_success');
  const authError = urlParams.get('error');

  console.log('Login useEffect - URL params:', {
    accessToken: !!accessToken,
    refreshToken: !!refreshToken,
    authSuccess,
    authError,
    currentUrl: window.location.href
  });

  // Handle OAuth error
  if (authError) {
    const errorMessages = {
      'google_auth_failed': 'Google authentication failed. Please try again.',
      'user_creation_failed': 'Failed to create user account. Please try again.',
      'session_creation_failed': 'Failed to create session. Please try again.',
      'token_creation_failed': 'Failed to create authentication tokens. Please try again.',
      'google_oauth_failed': 'Google OAuth failed. Please try again.'
    };

    setError(errorMessages[authError] || 'Authentication failed. Please try again.');

    // Clean URL
    const newUrl = window.location.pathname;
    window.history.replaceState(null, '', newUrl);
    return;
  }

  // Handle OAuth success with access tokens
  if (accessToken && authSuccess === 'true') {
    console.log('Processing successful OAuth tokens');

    // Store tokens FIRST
    localStorage.setItem('token', accessToken);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }

    // Clean URL BEFORE calling success handler to prevent reload loops
    const newUrl = window.location.pathname;
    window.history.replaceState(null, '', newUrl);

    // Call success handler after a small delay to ensure URL is cleaned
    setTimeout(() => {
      onLoginSuccess({
        access_token: accessToken,
        refresh_token: refreshToken
      });
    }, 100);

    return;
  }

      // Prevent body scroll when modal is open
      document.body.style.overflowY = 'hidden';

      return () => {
        document.body.style.overflowY = 'auto';
      };
    }, [onLoginSuccess]);

  const extractErrorMessage = (err) => {
    const detail = err?.response?.data?.detail ?? err?.message;
    if (!detail) return 'Failed to login. Please check your credentials.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const msgs = detail.map((d) => {
        const loc = Array.isArray(d?.loc) ? d.loc.join('.') : d?.loc;
        const msg = d?.msg || d?.message || JSON.stringify(d);
        return loc ? `${loc}: ${msg}` : msg;
      }).filter(Boolean);
      return msgs.join('\n') || 'Validation error';
    }
    try { return JSON.stringify(detail); } catch { return String(detail); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const body = { email, password, remember_me: rememberMe };
      const response = await apiClient.post('/auth/login', body);

      localStorage.setItem('token', response.data.access_token);
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      onLoginSuccess(response.data);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Add loading state for Google login
    setLoading(true);
    setError(null);

    // Save current location so we can return the user here after OAuth completes
    try { localStorage.setItem('postLoginRedirect', window.location.pathname + window.location.search); } catch (e) { /* ignore */ }

    // Redirect to backend Google OAuth endpoint
    window.location.href = `${config.API_BASE_URL}/auth/google/login`;
  };

  return (
    <div className="flex justify-center items-center min-h-screen px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className={`w-full max-w-md p-8 rounded-2xl border ${
          isDarkMode
            ? 'bg-gray-900/90 border-gray-700 shadow-[0_8px_30px_rgb(0,0,0,0.35)] backdrop-blur'
            : 'bg-white/95 border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.08)] backdrop-blur'
        }`}
      >
        {/* Header with Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src={require('../assets/logo.png')}
              alt="JobxBharat Logo"
              className="h-12 w-12 object-contain"
            />
            <h1 className="text-3xl font-extrabold">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-600">Jobx</span><span className="text-brand-saffron">Bharat</span>
            </h1>
          </div>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-lg`}>
            Welcome back! Sign in to continue
          </p>
        </div>

        {/* Google Sign In Button */}
        <motion.button
          type="button"
          onClick={handleGoogleLogin}
          disabled={loading}
          whileHover={{ scale: loading ? 1 : 1.02 }}
          whileTap={{ scale: loading ? 1 : 0.98 }}
          className={`w-full mb-6 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-3 transition-all duration-200 border ${
            loading 
              ? 'opacity-70 cursor-not-allowed' 
              : 'hover:bg-gray-50'
          } ${
            isDarkMode
              ? 'bg-white text-gray-900 border-gray-300'
              : 'bg-white text-gray-900 border-gray-300'
          }`}
        >
          <FcGoogle className="h-5 w-5" />
          {loading ? 'Connecting to Google...' : 'Continue with Google'}
        </motion.button>

        {/* Divider */}
        <div className="relative mb-6">
          <div className={`absolute inset-0 flex items-center`}>
            <div className={`w-full border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className={`px-2 ${isDarkMode ? 'bg-gray-900 text-gray-400' : 'bg-white text-gray-500'}`}>
              Or continue with email
            </span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaEnvelope className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={loading}
                className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all ${
                  isDarkMode
                    ? 'bg-gray-800 border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400'
                    : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black'
                } ${loading ? 'opacity-70' : ''}`}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaLock className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={loading}
                className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all ${
                  isDarkMode
                    ? 'bg-gray-800 border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400'
                    : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black'
                } ${loading ? 'opacity-70' : ''}`}
              />
            </div>
          </div>

          {/* Remember Me / Forgot Password */}
          <div className="flex items-center justify-between">
            <label className={`inline-flex items-center gap-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <input 
                type="checkbox" 
                checked={rememberMe} 
                onChange={(e) => setRememberMe(e.target.checked)} 
                disabled={loading}
                className="form-checkbox h-4 w-4" 
              />
              <span>Remember me</span>
            </label>
            <button
              type="button"
              onClick={onForgotPasswordClick}
              disabled={loading}
              className={`text-sm text-gray-800 dark:text-gray-300 hover:underline bg-transparent border-none p-0 m-0 cursor-pointer ${loading ? 'opacity-70' : ''}`}
            >
              Forgot Password?
            </button>
          </div>

          {/* Error */}
          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`${
                isDarkMode
                  ? 'bg-red-900/30 text-red-200'
                  : 'bg-red-100 text-red-700'
              } p-3 rounded-lg`}
            >
              {error}
            </motion.div>
          )}

          {/* Submit Button */}
          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: loading ? 1 : 0.98 }}
            className={`w-full py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl'}`}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </motion.button>

          {/* Register Link */}
          <div className="text-center mt-3">
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
              Don&apos;t have an account?{' '}
              <button
                type="button"
                onClick={onRegisterClick}
                disabled={loading}
                className={`text-gray-800 dark:text-gray-300 hover:underline bg-transparent border-none p-0 m-0 cursor-pointer ${loading ? 'opacity-70' : ''}`}
              >
                Register
              </button>
            </p>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default Login;